/**
 * Add question with AI: user fills Board/Year/Difficulty, Subject, Topic,
 * then instruction and count. AI generates question, correct answer, and detail answer.
 * Preview list for review before saving to DB. Reuses existing AI generation and bulk create.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../services/api';
import BoardYearPairs from './BoardYearPairs';
import AIGeneratedQuestionCard from './AIGeneratedQuestionCard';
import AIPreviewQuestionDialog from './AIPreviewQuestionDialog';
import { AI_FEEDBACK_UI_ENABLED, QUESTIONS_AI_EXTRACT_VISUAL_CONTEXT, QUESTIONS_AI_GENERATE, QUESTIONS_AI_GENERATE_VISUAL, QUESTIONS_BULK_CREATE } from '../../config/Config';
import FeedbackModal from '../FeedbackModal';
import { submitFeedback } from '../../api/feedback';
import { useGenerationTracking } from '../../hooks/useGenerationTracking';

/** Question type options for AI generation. VALUE is sent to API; Mixed uses note_to_ai for variety. */
export const QUESTION_TYPE_OPTIONS = [
  { value: 'TRUE_FALSE', label: 'True/False' },
  { value: 'MULTIPLE', label: 'Multiple Choice' },
  { value: 'SHORT_ANSWER', label: 'Short Answer' },
  { value: 'MIXED', label: 'Mixed' },
];

const initialForm = {
  board_years: [{ board: '', academic_year: '', difficulty: '' }],
  subject: '',
  topic: '',
  instruction: '',
  question_type: 'MULTIPLE',
  count: 3,
};

const QUESTION_FEEDBACK_TAGS = [
  { value: 'wrong_answer', label: 'Answer issue' },
  { value: 'too_difficult', label: 'Too difficult' },
  { value: 'too_easy', label: 'Too easy' },
  { value: 'wrong_topic_focus', label: 'Wrong focus' },
  { value: 'not_curriculum_aligned', label: 'Not curriculum aligned' },
  { value: 'too_similar', label: 'Too similar' },
];

const AI_NOTE_MAX_LENGTH = 250;

const compactText = (value, maxLength = AI_NOTE_MAX_LENGTH) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
};

const getApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  const firstKey = Object.keys(data)[0];
  const firstValue = firstKey ? data[firstKey] : null;
  if (Array.isArray(firstValue)) return `${firstKey}: ${firstValue.join('; ')}`;
  if (typeof firstValue === 'string') return `${firstKey}: ${firstValue}`;
  return fallback;
};

const ABSTRACT_VISUAL_TERMS = [
  'permutation',
  'probability',
  'equation',
  'expression',
  'combination',
  'arrangement',
  'sequence',
  'number',
  'integer',
  'digit',
];

const validateRealWorldSubject = (subject) => {
  const text = String(subject || '').trim();
  if (!text) {
    return {
      valid: false,
      message: 'No real-world image could be identified for this question. Please provide a visual instruction manually.',
    };
  }
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 3) {
    return {
      valid: false,
      message: 'Visual subject is too vague. Please provide a clearer instruction.',
    };
  }
  const lower = text.toLowerCase();
  const hasAbstractOnly = ABSTRACT_VISUAL_TERMS.some((term) => lower.includes(term))
    && !/(keypad|button|lock|wall|ladder|coin|dice|card|bag|box|graph|chart|triangle|circle|clock|ruler|shape|object|container|building|road|field|table)/i.test(text);
  if (hasAbstractOnly) {
    return {
      valid: false,
      message: 'This question may not have a natural real-world image. Consider using Math Diagram instead.',
    };
  }
  return { valid: true, message: '' };
};

export default function AddQuestionWithAIDialog({
  open,
  onClose,
  subjects = [],
  allTopics = [],
  boards = [],
  academicYears = [],
  curriculumAssociations = [],
  difficulties = [],
  onSuccess,
  onError,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [originalCandidates, setOriginalCandidates] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState(null);
  const [feedbackSessionId, setFeedbackSessionId] = useState('');
  const [questionFeedback, setQuestionFeedback] = useState({});
  const [questionRegenerationNotes, setQuestionRegenerationNotes] = useState({});
  const [regeneratingCandidateIndices, setRegeneratingCandidateIndices] = useState([]);
  const [visualOptions, setVisualOptions] = useState({ type: '', instruction: '' });
  const [candidateVisualInstructions, setCandidateVisualInstructions] = useState({});
  const [candidateVisualGenerating, setCandidateVisualGenerating] = useState({});
  const [candidateVisualErrors, setCandidateVisualErrors] = useState({});
  const { onGenerationStart, buildImplicitSignals, resetTracking } = useGenerationTracking();

  const topicsById = useMemo(() => {
    const map = new Map();
    (allTopics || []).forEach((topic) => {
      if (topic?.id != null) map.set(String(topic.id), topic);
    });
    return map;
  }, [allTopics]);

  const availableTopics = useMemo(() => {
    if (!form.subject) return [];
    const byId = new Map();
    (curriculumAssociations || []).forEach((assoc) => {
      if (
        assoc?.is_active === false ||
        String(assoc.subject) !== String(form.subject) ||
        assoc.topic == null ||
        assoc.parent_mapping_topic == null ||
        String(assoc.topic) === String(assoc.parent_mapping_topic)
      ) {
        return;
      }
      const masterTopic = topicsById.get(String(assoc.topic));
      if (!byId.has(String(assoc.topic))) {
        byId.set(String(assoc.topic), {
          id: assoc.topic,
          topic: assoc.topic_name || masterTopic?.topic || masterTopic?.name || '',
          name: assoc.topic_name || masterTopic?.name || masterTopic?.topic || '',
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => (a.topic || a.name || '').localeCompare(b.topic || b.name || ''));
  }, [curriculumAssociations, form.subject, topicsById]);

  const getChapterForPair = useCallback((pair) => {
    if (!form.subject || !form.topic || !pair?.board || !pair?.academic_year) return null;
    const assoc = (curriculumAssociations || []).find((item) => (
      item?.is_active !== false &&
      String(item.subject) === String(form.subject) &&
      String(item.topic) === String(form.topic) &&
      String(item.board) === String(pair.board) &&
      String(item.academic_year) === String(pair.academic_year) &&
      item.parent_mapping_topic != null
    ));
    if (!assoc) return null;
    const chapterTopic = topicsById.get(String(assoc.parent_mapping_topic));
    return {
      id: assoc.parent_mapping_topic,
      name: assoc.parent_mapping_topic_name || chapterTopic?.topic || chapterTopic?.name || '',
    };
  }, [curriculumAssociations, form.subject, form.topic, topicsById]);

  const isPairValidForSelectedTopic = useCallback((pair) => (
    Boolean(pair?.board && pair?.academic_year && pair?.difficulty && getChapterForPair(pair))
  ), [getChapterForPair]);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setLoading(false);
      setError('');
      setCandidates([]);
      setOriginalCandidates([]);
      setSelectedCandidates([]);
      setShowPreview(false);
      setPreviewQuestion(null);
      setFeedbackOpen(false);
      setPendingFeedback(null);
      setFeedbackSessionId('');
      setQuestionFeedback({});
      setQuestionRegenerationNotes({});
      setRegeneratingCandidateIndices([]);
      setVisualOptions({ type: '', instruction: '' });
      setCandidateVisualInstructions({});
      setCandidateVisualGenerating({});
      setCandidateVisualErrors({});
      resetTracking();
    }
  }, [open, resetTracking]);

  const handlePairsChange = (idx, field, value) => {
    setForm((prev) => {
      let arr = [...prev.board_years];
      if (field === 'add') arr.push({ board: '', academic_year: '', difficulty: '' });
      else if (field === 'remove') arr = arr.filter((_, i) => i !== idx);
      else if (field === 'replace') arr = value;
      else arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, board_years: arr };
    });
  };

  const isValidForGenerate = () => {
    const atLeastOnePair = form.board_years.some(isPairValidForSelectedTopic);
    const filledPairs = form.board_years.filter((pair) => (
      pair?.board || pair?.academic_year || pair?.difficulty
    ));
    const allFilledPairsAreValid = filledPairs.every(isPairValidForSelectedTopic);
    return atLeastOnePair && allFilledPairsAreValid && form.subject && form.topic && form.question_type && form.count >= 1 && form.count <= 5;
  };

  const buildSyntheticSourceQuestion = () => {
    const subjectObj = subjects.find((s) => String(s.id) === String(form.subject));
    const topicObj = availableTopics.find((t) => String(t.id) === String(form.topic));
    const firstPair = form.board_years.find(isPairValidForSelectedTopic);
    const boardObj = boards.find((b) => String(b.id) === String(firstPair?.board));
    const yearObj = academicYears.find((y) => String(y.id) === String(firstPair?.academic_year));
    const board_years = form.board_years
      .filter(isPairValidForSelectedTopic)
      .map((p) => ({ board: p.board, academic_year: p.academic_year, difficulty: p.difficulty }));

    const isMixed = form.question_type === 'MIXED';
    const questionTypeForApi = isMixed ? 'MULTIPLE' : form.question_type;
    return {
      subject: form.subject,
      topic: form.topic,
      subject_name: subjectObj?.name || '',
      topic_name: topicObj?.topic ?? topicObj?.name ?? '',
      board: boardObj?.name ?? boardObj?.code ?? String(firstPair?.board ?? ''),
      year: yearObj?.name ?? yearObj?.year ?? String(firstPair?.academic_year ?? ''),
      question_text: form.instruction.trim() || 'Generate questions appropriate for the given subject and topic.',
      question_type: questionTypeForApi,
      board_years,
      academic_years: board_years.map((by) => ({
        board_id: by.board,
        id: by.academic_year,
        difficulty_id: by.difficulty,
      })),
    };
  };

  const buildFeedbackContext = () => {
    const subjectObj = subjects.find((s) => String(s.id) === String(form.subject));
    const topicObj = availableTopics.find((t) => String(t.id) === String(form.topic));
    const firstPair = form.board_years.find(isPairValidForSelectedTopic);
    const boardObj = boards.find((b) => String(b.id) === String(firstPair?.board));
    const yearObj = academicYears.find((y) => String(y.id) === String(firstPair?.academic_year));
    const difficultyObj = difficulties.find((d) => String(d.id) === String(firstPair?.difficulty));
    return {
      module: 'question_gen',
      board: boardObj?.name || boardObj?.code || '',
      subject: subjectObj?.name || '',
      topic: topicObj?.topic || topicObj?.name || '',
      grade_level: yearObj?.label || yearObj?.year || '',
      difficulty: difficultyObj?.name || '',
    };
  };

  const buildQuestionCharacteristics = (question, issueTags = []) => ({
    question_type: question?.question_type || form.question_type || 'unknown',
    topic_name: question?.topic_name || buildFeedbackContext().topic || '',
    issue_tags: issueTags,
    visual_type: question?._visual_type || '',
    has_visual: Boolean(question?.image_url || question?.image_proxy_url),
    visual_descriptor: question?._visual_descriptor || '',
    has_options: Array.isArray(question?.options) && question.options.length > 0,
    option_count: Array.isArray(question?.options) ? question.options.length : 0,
    has_correct_answer: Boolean((question?.correct_answer || '').trim()),
    has_detail_answer: Boolean((question?.detail_answer || '').trim()),
    question_length: String(question?.question_text || '').length,
    answer_length: String(question?.correct_answer || '').length,
    detail_length: String(question?.detail_answer || '').length,
  });

  const toggleQuestionFeedbackTag = (index, tag) => {
    setQuestionFeedback((prev) => {
      const current = prev[index] || { tags: [], note: '' };
      const tags = current.tags.includes(tag)
        ? current.tags.filter((item) => item !== tag)
        : [...current.tags, tag];
      return { ...prev, [index]: { ...current, tags } };
    });
  };

  const updateQuestionFeedbackNote = (index, note) => {
    setQuestionFeedback((prev) => {
      const current = prev[index] || { tags: [], note: '' };
      return { ...prev, [index]: { ...current, note: note.slice(0, 500) } };
    });
  };

  const updateQuestionRegenerationNote = (index, note) => {
    setQuestionRegenerationNotes((prev) => ({ ...prev, [index]: note.slice(0, 500) }));
  };

  const buildRegenerationInstruction = (question, note, tags) => {
    const parts = [];
    if (form.instruction.trim()) {
      parts.push(`Original teacher instruction: ${form.instruction.trim()}`);
    }
    if (tags.length) {
      parts.push(`Issues to fix for this generated question: ${tags.join(', ')}`);
    }
    if (note.trim()) {
      parts.push(`Regeneration instruction for this question: ${note.trim()}`);
    }
    parts.push('Regenerate only one replacement question. Keep the same board, grade, subject, topic, and difficulty.');
    parts.push(`Previous generated question to improve: ${question?.question_text || ''}`);
    if (question?.correct_answer) {
      parts.push(`Previous answer to improve: ${question.correct_answer}`);
    }
    if (question?.detail_answer) {
      parts.push(`Previous worked solution to improve: ${question.detail_answer}`);
    }
    return parts.join('\n');
  };

  const stripInternalQuestionFields = (question) => {
    const {
      _visual_type,
      _visual_instruction,
      _visual_descriptor,
      _visual_cache_key,
      _visual_context,
      _visual_notice,
      _visual_warning,
      _visual_subject,
      _visual_status,
      ...cleanQuestion
    } = question || {};
    return cleanQuestion;
  };

  const buildCompactRegenerationNote = (note, tags) => {
    const parts = ['Regenerate one replacement question.'];
    if (note.trim()) {
      parts.push(`Instruction: ${note.trim()}`);
    }
    if (tags.length) {
      parts.push(`Fix: ${tags.join(', ')}`);
    }
    parts.push('Keep board, grade, subject, topic, difficulty.');
    return compactText(parts.join(' '));
  };

  const handleRegenerateCandidate = async (question, index) => {
    const feedback = questionFeedback[index] || { tags: [], note: '' };
    const note = questionRegenerationNotes[index] || feedback.note || '';
    const tags = feedback.tags || [];
    const idempotencyBase = feedbackSessionId || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const attemptKey = `${idempotencyBase}:regen:${index}:${Date.now()}`;

    setRegeneratingCandidateIndices((prev) => [...prev, index]);
    setError('');

    try {
      await submitFeedback(
        buildFeedbackContext(),
        {
          outcome: 'regenerated',
          regeneration_count: 1,
          teacher_edited: true,
          edit_description: tags.length
            ? `Regenerated generated question due to ${tags.join(', ')}`
            : 'Regenerated generated question with teacher instruction',
        },
        {
          tags: [...new Set([...tags, 'single_question_regenerated'])],
          teacher_note: note.trim().slice(0, 500),
          question_ref_id: `candidate-${index + 1}`,
          idempotency_key: `question-gen:${attemptKey}:feedback`,
          question_characteristics: buildQuestionCharacteristics(question, tags),
          original_compact: question,
        }
      );

      const source_question = {
        ...buildSyntheticSourceQuestion(),
        question_text: buildRegenerationInstruction(question, note, tags),
      };
      const firstPair = form.board_years.find(isPairValidForSelectedTopic);
      const difficultyObj = difficulties.find((d) => String(d.id) === String(firstPair?.difficulty));
      const difficulty = difficultyObj?.name || 'Basic';
      const response = await api.post(QUESTIONS_AI_GENERATE, {
        source_question,
        difficulty,
        count: 1,
        note_to_ai: buildCompactRegenerationNote(note, tags),
      });
      const replacement = normalizeGeneratedCandidate(response.data?.candidates?.[0]);
      if (!replacement) {
        throw new Error('AI did not return a replacement question.');
      }

      setCandidates((prev) => prev.map((item, itemIndex) => (
        itemIndex === index ? replacement : item
      )));
      setQuestionRegenerationNotes((prev) => ({ ...prev, [index]: '' }));
      setCandidateVisualInstructions((prev) => ({ ...prev, [index]: visualOptions.instruction || '' }));
    } catch (err) {
      console.error('AI single question regeneration error:', err);
      const msg = getApiErrorMessage(err, 'Failed to regenerate this question. Please try again.');
      setError(msg);
      onError?.(msg);
    } finally {
      setRegeneratingCandidateIndices((prev) => prev.filter((item) => item !== index));
    }
  };

  const updateCandidateVisualInstruction = (index, instruction) => {
    setCandidateVisualInstructions((prev) => ({ ...prev, [index]: instruction.slice(0, 500) }));
  };

  const buildVisualInstruction = (question, index) => {
    const explicit = candidateVisualInstructions[index] ?? question?._visual_subject ?? visualOptions.instruction;
    if (explicit && explicit.trim()) return explicit.trim();
    const topic = question?.topic_name || buildFeedbackContext().topic || 'the selected topic';
    return `Simple educational visual for ${topic}`;
  };

  const normalizeGeneratedCandidate = (candidate) => {
    if (!candidate || typeof candidate !== 'object') return candidate;
    const detailAnswer = candidate.detail_answer
      || candidate.detailAnswer
      || candidate.detailed_answer
      || candidate.detailed_solution
      || candidate.worked_solution
      || candidate.solution
      || candidate.explanation
      || '';
    return {
      ...candidate,
      detail_answer: detailAnswer,
    };
  };

  const indexVisualContexts = (results = []) => {
    const map = new Map();
    (results || []).forEach((item, fallbackIndex) => {
      const rawIndex = item?.question_index ?? item?.index ?? fallbackIndex;
      const index = Number(rawIndex);
      if (Number.isFinite(index)) {
        map.set(index, item);
      }
    });
    return map;
  };

  const prepareCandidatesWithVisualContext = async (generatedCandidates) => {
    if (!visualOptions.type || !generatedCandidates.length) {
      return generatedCandidates;
    }

    try {
      const response = await api.post(QUESTIONS_AI_EXTRACT_VISUAL_CONTEXT, {
        visual_instruction: visualOptions.instruction.trim() || '',
        questions: generatedCandidates.map((question, index) => ({
          index,
          text: question?.question_text || '',
        })),
      });
      const contextByIndex = indexVisualContexts(response.data?.results || []);
      const nextInstructions = {};
      const prepared = generatedCandidates.map((question, index) => {
        const visualContext = contextByIndex.get(index) || {};
        if (visualOptions.type === 'diagram') {
          if (visualContext.diagram_relevant === false) {
            return {
              ...question,
              _visual_context: visualContext,
              _visual_status: 'skipped',
              _visual_notice: 'Math diagram is not relevant for this question',
              _visual_subject: '',
            };
          }
          const diagramSubject = visualOptions.instruction.trim()
            || visualContext.diagram_type
            || question.topic_name
            || buildFeedbackContext().topic
            || 'math diagram';
          nextInstructions[index] = diagramSubject;
          return {
            ...question,
            _visual_context: visualContext,
            _visual_status: 'ready_to_generate',
            _visual_notice: visualContext.diagram_type ? `Diagram context: ${visualContext.diagram_type}` : '',
            _visual_subject: diagramSubject,
          };
        }

        const extractedSubject = String(visualContext.realworld_subject || '').trim();
        const validation = validateRealWorldSubject(extractedSubject);
        if (extractedSubject) {
          nextInstructions[index] = extractedSubject;
        }
        return {
          ...question,
          _visual_context: visualContext,
          _visual_status: validation.valid ? 'ready_to_generate' : 'needs_override',
          _visual_notice: validation.valid ? `Will generate: ${extractedSubject}` : '',
          _visual_warning: validation.valid ? '' : validation.message,
          _visual_subject: extractedSubject,
        };
      });
      setCandidateVisualInstructions(nextInstructions);
      return prepared;
    } catch (err) {
      console.error('AI visual context extraction error:', err);
      const msg = getApiErrorMessage(err, 'Visual context could not be extracted. You can still generate questions without visuals.');
      setCandidateVisualErrors((prev) => ({ ...prev, batch: msg }));
      return generatedCandidates;
    }
  };

  const handleGenerateCandidateVisual = async (route, question, index) => {
    const hasExplicitRealWorldSubject = String(candidateVisualInstructions[index] ?? question?._visual_subject ?? visualOptions.instruction ?? '').trim();
    if (route === 'real_world' && !hasExplicitRealWorldSubject) {
      const msg = 'Please provide a visual subject before generating an image.';
      setCandidateVisualErrors((prev) => ({ ...prev, [index]: msg }));
      return;
    }
    const visualInstruction = buildVisualInstruction(question, index);
    if (route === 'real_world') {
      const validation = validateRealWorldSubject(visualInstruction);
      if (!validation.valid) {
        setCandidateVisualErrors((prev) => ({ ...prev, [index]: validation.message }));
        return;
      }
    }
    setCandidateVisualGenerating((prev) => ({ ...prev, [index]: route }));
    setCandidateVisualErrors((prev) => ({ ...prev, [index]: '' }));

    try {
      const response = await api.post(QUESTIONS_AI_GENERATE_VISUAL, {
        route,
        visual_instruction: visualInstruction,
        question_context: {
          subject: buildFeedbackContext().subject,
          topic: question?.topic_name || buildFeedbackContext().topic,
          question_type: question?.question_type || form.question_type,
          question_text: question?.question_text || '',
          correct_answer: question?.correct_answer || '',
          options: Array.isArray(question?.options) ? question.options.slice(0, 4) : [],
        },
      });
      const storageUrl = response.data?.storage_url || response.data?.url;
      if (!storageUrl) {
        throw new Error('Visual generation did not return an image URL.');
      }

      const descriptor = response.data?.visual_descriptor || `[Visual: ${route}]`;
      setCandidates((prev) => prev.map((item, itemIndex) => (
        itemIndex === index
          ? {
              ...item,
              image_url: storageUrl,
              image_proxy_url: response.data?.url || storageUrl,
              _visual_type: route,
              _visual_instruction: visualInstruction,
              _visual_descriptor: descriptor,
              _visual_cache_key: response.data?.cache_key || '',
            }
          : item
      )));
      await submitFeedback(
        buildFeedbackContext(),
        {
          outcome: 'regenerated',
          regeneration_count: 1,
          teacher_edited: false,
          edit_description: `Generated ${route === 'diagram' ? 'math diagram' : 'real world image'} visual`,
        },
        {
          tags: ['visual_generated', route],
          teacher_note: visualInstruction.slice(0, 500),
          question_ref_id: `candidate-${index + 1}`,
          idempotency_key: feedbackSessionId ? `question-gen:${feedbackSessionId}:visual:${index}:${route}:${response.data?.cache_key || Date.now()}` : undefined,
          question_characteristics: buildQuestionCharacteristics(
            { ...question, image_url: storageUrl, _visual_type: route, _visual_descriptor: descriptor },
            ['visual_generated', route]
          ),
        }
      );
    } catch (err) {
      console.error('AI visual generation error:', err);
      const msg = getApiErrorMessage(err, 'Visual generation failed. Please try again.');
      setCandidateVisualErrors((prev) => ({ ...prev, [index]: msg }));
      onError?.(msg);
    } finally {
      setCandidateVisualGenerating((prev) => ({ ...prev, [index]: '' }));
    }
  };

  const handleRemoveCandidateVisual = (index) => {
    setCandidates((prev) => prev.map((item, itemIndex) => (
      itemIndex === index
        ? {
            ...item,
            image_url: '',
            image_proxy_url: '',
            _visual_type: '',
            _visual_instruction: '',
            _visual_descriptor: '',
            _visual_cache_key: '',
            _visual_notice: '',
            _visual_warning: '',
          }
        : item
    )));
  };

  const handleGenerate = async () => {
    if (!isValidForGenerate()) {
      setError('Please select Subject, Topic, and only Board/Year/Difficulty rows that have a curriculum chapter for that topic.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      onGenerationStart();
      const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      setFeedbackSessionId(sessionId);
      const source_question = buildSyntheticSourceQuestion();
      const firstPair = form.board_years.find(isPairValidForSelectedTopic);
      const difficultyObj = difficulties.find((d) => String(d.id) === String(firstPair?.difficulty));
      const difficulty = difficultyObj?.name || 'Basic';

      const response = await api.post(QUESTIONS_AI_GENERATE, {
        source_question,
        difficulty,
        count: Math.min(5, Math.max(1, Number(form.count) || 1)),
        note_to_ai: compactText(form.instruction) || null,
      });

      const generatedCandidates = (response.data.candidates || []).map(normalizeGeneratedCandidate);
      setQuestionFeedback({});
      setQuestionRegenerationNotes({});
      setCandidateVisualInstructions({});
      setCandidateVisualGenerating({});
      setCandidateVisualErrors({});
      const preparedCandidates = await prepareCandidatesWithVisualContext(generatedCandidates);
      setCandidates(preparedCandidates);
      setOriginalCandidates(preparedCandidates);
      setSelectedCandidates(preparedCandidates.map((_, i) => i));
      setQuestionFeedback({});
      setQuestionRegenerationNotes({});
      setShowPreview(true);
      if (visualOptions.type === 'diagram') {
        preparedCandidates.forEach((question, index) => {
          if (question?._visual_status === 'ready_to_generate') {
            handleGenerateCandidateVisual('diagram', question, index);
          }
        });
      }
    } catch (err) {
      console.error('AI generation error:', err);
      const msg = err.response?.status === 429
        ? 'Rate limit exceeded. Please try again later.'
        : err.response?.status === 408
          ? 'Request timed out. Please try again.'
          : err.response?.data?.error
            ? err.response.data.error
            : 'Failed to generate questions. Please try again.';
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateToggle = (index) => {
    setSelectedCandidates((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleSaveSelected = async () => {
    if (selectedCandidates.length === 0) {
      setError('Please select at least one question to save.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const questionsToSave = selectedCandidates.map((i) => stripInternalQuestionFields(candidates[i]));
      const response = await api.post(QUESTIONS_BULK_CREATE, { questions: questionsToSave });
      const createdIds = response.data.created_ids || [];
      const originalSelected = selectedCandidates.map((i) => originalCandidates[i]).filter(Boolean);
      const implicitSignals = buildImplicitSignals('accepted', originalSelected, questionsToSave);
      setPendingFeedback({
        context: buildFeedbackContext(),
        implicitSignals: {
          ...implicitSignals,
          idempotency_key: feedbackSessionId ? `question-gen:${feedbackSessionId}:save` : undefined,
          original_compact: originalSelected,
          edited_compact: questionsToSave,
        },
      });
      onSuccess?.(createdIds.length);
      if (AI_FEEDBACK_UI_ENABLED) {
        setFeedbackOpen(true);
      } else {
        await submitFeedback(buildFeedbackContext(), {
          ...implicitSignals,
          idempotency_key: feedbackSessionId ? `question-gen:${feedbackSessionId}:save` : undefined,
          original_compact: originalSelected,
          edited_compact: questionsToSave,
        }, {});
        onClose();
      }

      const perQuestionFeedback = selectedCandidates
        .map((candidateIndex) => {
          const feedback = questionFeedback[candidateIndex] || { tags: [], note: '' };
          return {
            candidateIndex,
            question: candidates[candidateIndex],
            tags: feedback.tags || [],
            note: (feedback.note || '').trim().slice(0, 500),
          };
        })
        .filter((item) => item.tags.length || item.note);

      await Promise.all(perQuestionFeedback.map((item) => submitFeedback(
        buildFeedbackContext(),
        {
          outcome: 'edited',
          regeneration_count: 0,
          teacher_edited: true,
          edit_description: item.tags.length ? `Generated question feedback: ${item.tags.join(', ')}` : 'Generated question feedback note',
        },
        {
          tags: item.tags,
          teacher_note: item.note,
          question_ref_id: `candidate-${item.candidateIndex + 1}`,
          idempotency_key: feedbackSessionId ? `question-gen:${feedbackSessionId}:candidate:${item.candidateIndex}` : undefined,
          question_characteristics: buildQuestionCharacteristics(item.question, item.tags),
        }
      )));
    } catch (err) {
      console.error('Bulk create error:', err);
      const msg = err.response?.data?.questions
        ? (Array.isArray(err.response.data.questions)
            ? err.response.data.questions.join('; ')
            : err.response.data.questions)
        : err.response?.data?.error
          ? err.response.data.error
          : 'Failed to save questions. Please try again.';
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    if (candidates.length > 0) {
      submitFeedback(
        buildFeedbackContext(),
        {
          ...buildImplicitSignals('regenerated', originalCandidates, candidates),
          idempotency_key: feedbackSessionId ? `question-gen:${feedbackSessionId}:discard` : undefined,
        },
        {}
      );
    }
    setShowPreview(false);
    setCandidates([]);
    setOriginalCandidates([]);
    setSelectedCandidates([]);
    setQuestionFeedback({});
    setQuestionRegenerationNotes({});
  };

  const finishAfterFeedback = () => {
    setFeedbackOpen(false);
    setPendingFeedback(null);
    onClose();
  };

  const handleFeedbackSubmit = async (explicitSignals) => {
    if (pendingFeedback) {
      await submitFeedback(pendingFeedback.context, pendingFeedback.implicitSignals, explicitSignals);
    }
    finishAfterFeedback();
  };

  const handleFeedbackSkip = async () => {
    if (pendingFeedback) {
      await submitFeedback(pendingFeedback.context, pendingFeedback.implicitSignals, {});
    }
    finishAfterFeedback();
  };

  const handleDialogClose = useCallback((event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose?.();
  }, [onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            minHeight: { xs: '100dvh', sm: '60vh' },
            maxHeight: { xs: '100dvh', sm: '90vh' },
            minWidth: { sm: 720 },
            width: { xs: '100%', sm: 900 },
            maxWidth: { xs: '100%', sm: 'calc(100vw - 32px)' },
            m: { xs: 0, sm: 2 },
            overflow: 'auto',
            resize: { xs: 'none', sm: 'both' },
          },
        }}
        transitionDuration={0}
        TransitionProps={{ timeout: { enter: 0, exit: 0 } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <AutoAwesomeIcon color="secondary" />
          <Typography component="span" variant="h6" sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
            Add question with AI
          </Typography>
          <Button
            onClick={onClose}
            sx={{ ml: 'auto', flexShrink: 0, minWidth: { xs: 0, sm: 64 }, px: { xs: 1, sm: 2 } }}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            overflowX: 'hidden',
          }}
        >
          {!showPreview ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Fill in the fields below (same as Add New Question). Then add an instruction and number of questions for AI to generate.
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Subject and topic are required before adding board/year rows.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField
                  select
                  label="Subject *"
                  value={form.subject}
                  onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value, topic: '', board_years: [{ board: '', academic_year: '', difficulty: '' }] }))}
                  fullWidth
                >
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  options={availableTopics}
                  getOptionLabel={(option) => {
                    return option.topic ?? option.name ?? '';
                  }}
                  value={availableTopics.find((t) => String(t.id) === String(form.topic)) ?? null}
                  onChange={(e, newValue) =>
                    setForm((p) => ({ ...p, topic: newValue ? newValue.id : '', board_years: [{ board: '', academic_year: '', difficulty: '' }] }))
                  }
                  isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                  disabled={!form.subject}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Topic *"
                      placeholder="Type to search topics..."
                    />
                  )}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const search = inputValue.toLowerCase();
                    const nameOnly = (opt) => (opt.topic ?? opt.name ?? '').toLowerCase();
                    return options.filter((opt) => nameOnly(opt).includes(search));
                  }}
                  noOptionsText="No topics found"
                />
              </Box>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Board & Academic Year *
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                At least one board-year-difficulty pair with all fields selected is required.
              </Typography>
              <BoardYearPairs
                pairs={form.board_years}
                boards={boards}
                years={academicYears}
                difficulties={difficulties}
                onChange={handlePairsChange}
                getChapterForPair={getChapterForPair}
                requireChapter
                chapterDisabledReason="Select board and year to resolve chapter"
              />

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Instruction & number of questions
              </Typography>
              <TextField
                label="Instruction for AI"
                value={form.instruction}
                onChange={(e) => setForm((p) => ({ ...p, instruction: e.target.value }))}
                multiline
                rows={3}
                fullWidth
                placeholder="e.g. Focus on word problems involving fractions, or Use two-digit numbers"
                helperText="Describe what kind of questions you want. Optional but recommended."
                sx={{ mb: 2 }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Optional visuals are generated separately after questions are created.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: visualOptions.type ? 1 : 0 }}>
                  <Button
                    variant={visualOptions.type === 'diagram' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setVisualOptions((prev) => ({ ...prev, type: prev.type === 'diagram' ? '' : 'diagram' }))}
                  >
                    + Math Diagram
                  </Button>
                  <Button
                    variant={visualOptions.type === 'real_world' ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setVisualOptions((prev) => ({ ...prev, type: prev.type === 'real_world' ? '' : 'real_world' }))}
                  >
                    + Real World Image
                  </Button>
                </Box>
                {visualOptions.type && (
                  <TextField
                    label="Visual note (optional)"
                    value={visualOptions.instruction}
                    onChange={(event) => setVisualOptions((prev) => ({ ...prev, instruction: event.target.value.slice(0, 500) }))}
                    fullWidth
                    size="small"
                    placeholder="Add visual guidance after the question context, e.g. use locks and digit slots"
                    helperText={`${visualOptions.instruction.length}/500`}
                  />
                )}
              </Box>
              <TextField
                select
                required
                label="Question type *"
                value={form.question_type}
                onChange={(e) => setForm((p) => ({ ...p, question_type: e.target.value }))}
                fullWidth
                helperText="Type of questions for AI to generate"
                sx={{ mb: 2 }}
              >
                {QUESTION_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Number of questions"
                type="number"
                value={form.count}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setForm((p) => ({ ...p, count: 1 }));
                    return;
                  }
                  const num = Math.round(Number(raw));
                  if (!Number.isFinite(num)) return;
                  const clamped = Math.min(5, Math.max(1, num));
                  setForm((p) => ({ ...p, count: clamped }));
                }}
                onKeyDown={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                inputProps={{ min: 1, max: 5, step: 1 }}
                helperText="1–5 questions (use arrows to change)"
                fullWidth
                sx={{ mb: 2 }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  Review generated questions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCandidates.length} of {candidates.length} selected
                </Typography>
              </Box>

              {candidates.map((question, index) => (
                <AIGeneratedQuestionCard
                  key={index}
                  question={question}
                  index={index}
                  isSelected={selectedCandidates.includes(index)}
                  onToggle={handleCandidateToggle}
                  onViewPreview={(q) => setPreviewQuestion(q)}
                  showDetailAnswer
                  editable
                  feedbackTags={AI_FEEDBACK_UI_ENABLED ? QUESTION_FEEDBACK_TAGS : []}
                  selectedFeedbackTags={questionFeedback[index]?.tags || []}
                  feedbackNote={questionFeedback[index]?.note || ''}
                  onToggleFeedbackTag={(tag) => toggleQuestionFeedbackTag(index, tag)}
                  onFeedbackNoteChange={(note) => updateQuestionFeedbackNote(index, note)}
                  regenerationInstruction={questionRegenerationNotes[index] || ''}
                  onRegenerationInstructionChange={(note) => updateQuestionRegenerationNote(index, note)}
                  onRegenerate={() => handleRegenerateCandidate(question, index)}
                  regenerating={regeneratingCandidateIndices.includes(index)}
                  allowDiagramVisual={visualOptions.type === 'diagram'}
                  allowRealWorldVisual={visualOptions.type === 'real_world'}
                  visualInstruction={candidateVisualInstructions[index] ?? question._visual_subject ?? visualOptions.instruction}
                  onVisualInstructionChange={(instruction) => updateCandidateVisualInstruction(index, instruction)}
                  onGenerateVisual={(route) => handleGenerateCandidateVisual(route, question, index)}
                  onRemoveVisual={() => handleRemoveCandidateVisual(index)}
                  visualGenerating={candidateVisualGenerating[index] || ''}
                  visualError={candidateVisualErrors[index] || ''}
                  onFieldChange={(field, value) => {
                    setCandidates((prev) => {
                      const next = prev.map((q, i) =>
                        i === index ? { ...q, [field]: value } : q
                      );
                      return next;
                    });
                  }}
                />
              ))}

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 2 },
            gap: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            '& > button': {
              minHeight: 42,
              flex: { xs: '1 1 0', sm: '0 0 auto' },
              whiteSpace: 'nowrap',
            },
          }}
        >
          {!showPreview ? (
            <>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={loading || !isValidForGenerate()}
                startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              >
                {loading ? 'Generating...' : 'Generate questions'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleDiscard} disabled={loading}>
                Discard
              </Button>
              <Button onClick={() => setShowPreview(false)} disabled={loading}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveSelected}
                disabled={loading || selectedCandidates.length === 0}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                sx={{
                  flex: { xs: '0 0 auto', sm: '0 0 auto' },
                  minWidth: { xs: 118, sm: 150 },
                  px: { xs: 2, sm: 2.5 },
                  '& .MuiButton-startIcon': { mr: 0.75 },
                }}
              >
                {loading ? 'Saving...' : `Save(${selectedCandidates.length})`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <AIPreviewQuestionDialog
        open={Boolean(previewQuestion)}
        onClose={() => setPreviewQuestion(null)}
        question={previewQuestion}
      />
      <FeedbackModal
        isOpen={feedbackOpen}
        onSubmit={handleFeedbackSubmit}
        onSkip={handleFeedbackSkip}
      />
    </>
  );
}
